import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '@charitystack/shared';
import { Label } from '@/components/ui/label';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { normalizeDomain } from '../../utils/urlElement';
import { getTabs, type OverlayType } from '../../utils/overlaySnippetTabs';
import { PagePathInput } from '@/components/ui/page-path-input';

type OverlayVariant = 'form' | 'url-element';

type OverlayLayout = 'default' | 'sharing';

interface OverlaySnippetsProps {
  elementID: string;
  /** The connected form ID. Required for url-element variant to include both formid and elementid in URLs. */
  formID?: string;
  type?: OverlayType;
  websiteUrl?: string;
  color?: string;
  /** Controls layout and behavior. 'form' shows domain input; 'url-element' hides it and uses elementid URL format. */
  variant?: OverlayVariant;
  /** Override the default "Overlay Trigger" title. */
  title?: string;
  /** Override the default tooltip content. Pass `null` to hide the tooltip entirely. */
  tooltipContent?: ReactNode | null;
  /** External page path (used by url-element variant instead of internal state). */
  pagePath?: string;
  /**
   * `sharing` — Link/Button toggle + compact snippet row (used inside FormSharingCards; header is in the parent).
   * `default` — full tabbed UI with title row.
   */
  layout?: OverlayLayout;
  /**
   * When false, hides the title row (Label + info). Use with `layout="sharing"` so the card header is the only title;
   * also guards against duplicate titles if the layout prop fails to apply at runtime.
   */
  showHeader?: boolean;
}

type TopTab = 'url' | 'html' | 'react' | 'prompt';

const TOP_TABS: { key: TopTab; label: string }[] = [
  { key: 'url', label: 'URL' },
  { key: 'html', label: 'HTML' },
  { key: 'react', label: 'React' },
  { key: 'prompt', label: 'Prompt' },
];

const CopyIcon = () => (
  <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

function selectSnippetNode(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

type SharingButtonTab = 'html' | 'react' | 'prompt';

export default function OverlaySnippets({
  elementID,
  formID,
  type = 'donate',
  websiteUrl,
  color,
  variant = 'form',
  title,
  tooltipContent,
  pagePath: externalPagePath,
  layout = 'default',
  showHeader = true,
}: OverlaySnippetsProps) {
  const [activeTopTab, setActiveTopTab] = useState<TopTab>('url');
  const [htmlSubTab, setHtmlSubTab] = useState<string>('html-link');
  const [reactSubTab, setReactSubTab] = useState<string>('react-link');
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [sharingMode, setSharingMode] = useState<'link' | 'button'>('link');
  const [sharingButtonTab, setSharingButtonTab] = useState<SharingButtonTab>('html');
  const [localPagePath, setLocalPagePath] = useState('');

  const isUrlElement = variant === 'url-element';
  const pagePath = isUrlElement ? (externalPagePath || '') : localPagePath;

  const hasWebsiteUrl = Boolean(websiteUrl?.trim());
  const normalizedDomain = hasWebsiteUrl ? normalizeDomain(websiteUrl) : '';
  const tabs = getTabs({
    elementID,
    formID,
    type,
    domain: websiteUrl,
    pagePath,
    color,
    useElementIDFormat: isUrlElement,
  });

  // Resolve the active snippet key based on top tab + sub tab
  const activeKey =
    activeTopTab === 'html' ? htmlSubTab : activeTopTab === 'react' ? reactSubTab : activeTopTab; // 'url' or 'prompt'

  const active = tabs.find((t) => t.key === activeKey) || tabs[0];
  const subTabs = tabs.filter((t) => t.group === activeTopTab);
  const hasSubTabs = activeTopTab === 'html' || activeTopTab === 'react';

  // Page path input for form variant (local state, not persisted)
  const pagePathInput = hasWebsiteUrl && !isUrlElement && (
    <PagePathInput
      domain={normalizedDomain}
      value={localPagePath}
      onChange={setLocalPagePath}
      size="sm"
    />
  );

  const handleCopy = async () => {
    const success = await copyToClipboard(active.snippet);
    if (success) {
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy');
    }
  };

  const sharingActiveKey: string =
    sharingMode === 'link'
      ? 'url'
      : sharingButtonTab === 'html'
        ? 'html-button'
        : sharingButtonTab === 'react'
          ? 'react-button'
          : 'prompt';

  const activeSharing = tabs.find((t) => t.key === sharingActiveKey) || tabs[0];

  const handleCopySharing = async () => {
    const success = await copyToClipboard(activeSharing.snippet);
    if (success) {
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy');
    }
  };

  // Inline warning when no website URL is configured.
  // Use a plain <a> (full-page navigation) instead of react-router <Link> so the
  // surrounding Sheet/Drawer unmounts cleanly — react-router-dom v5 has no
  // `reloadDocument` prop, and SPA navigation alone leaves the drawer covering
  // the new route.
  const noDomainWarning = !hasWebsiteUrl && !isUrlElement && (
    <p className="text-xs text-ink-muted mb-2">
      Overlay options require a Website URL. Configure it in{' '}
      <a
        href="/settings"
        className="text-ink-secondary underline hover:text-ink"
      >
        Settings &rarr; Organization
      </a>
      .
    </p>
  );

  if (layout === 'sharing') {
    const snippetId = `overlay-share-${elementID}-${sharingActiveKey}`;
    const isPromptSharing = sharingMode === 'button' && sharingButtonTab === 'prompt';

    return (
      <div>
        {noDomainWarning}
        <div className="mb-2.5 flex rounded-md border border-edge bg-paper-inset p-0.5">
          <button
            type="button"
            onClick={() => setSharingMode('link')}
            className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
              sharingMode === 'link'
                ? 'border-edge bg-paper-light text-ink-secondary'
                : 'border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            <svg
              className="h-3 w-3 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              strokeLinecap="round"
            >
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            Link
          </button>
          <button
            type="button"
            onClick={() => setSharingMode('button')}
            className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
              sharingMode === 'button'
                ? 'border-edge bg-paper-light text-ink-secondary'
                : 'border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            <svg
              className="h-3 w-3 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              strokeLinecap="round"
            >
              <rect x="4" y="8" width="16" height="8" rx="2" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Button
          </button>
        </div>

        {pagePathInput}

        {sharingMode === 'link' && (
          <div className="flex gap-1">
            <div
              role="textbox"
              aria-readonly="true"
              tabIndex={0}
              className="flex min-h-[2.25rem] flex-1 cursor-text items-center rounded-md border border-edge bg-paper-inset px-2.5 py-1.5 font-mono text-[11px] text-ink-secondary break-all select-all"
              onClick={(e) => {
                e.stopPropagation();
                selectSnippetNode(snippetId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  selectSnippetNode(snippetId);
                }
              }}
            >
              <span id={snippetId}>{activeSharing.snippet}</span>
            </div>
            {activeSharing.snippet.startsWith('http') && (
              <button
                type="button"
                className="flex items-center justify-center rounded-md border border-edge px-1.5 text-ink-muted transition-colors hover:bg-paper-inset hover:text-ink-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(activeSharing.snippet, '_blank', 'noopener,noreferrer');
                }}
                title="Open in New Tab"
              >
                <svg
                  className="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                </svg>
              </button>
            )}
            <button
              type="button"
              className="flex items-center gap-1 rounded-md border border-edge px-2 text-[11px] font-medium text-ink-muted transition-colors hover:bg-paper-inset hover:text-ink-secondary"
              onClick={(e) => {
                e.stopPropagation();
                void handleCopySharing();
              }}
              title="Copy to clipboard"
            >
              <CopyIcon />
              Copy
            </button>
          </div>
        )}

        {sharingMode === 'button' && (
          <>
            <div className="mb-2 flex rounded border border-edge bg-paper-inset p-px">
              <button
                type="button"
                onClick={() => {
                  setSharingButtonTab('html');
                  setPromptExpanded(false);
                }}
                className={`flex-1 rounded-sm border p-1 text-[10px] font-medium transition-colors ${
                  sharingButtonTab === 'html'
                    ? 'border-edge bg-paper-light text-ink-secondary'
                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                }`}
              >
                HTML Button
              </button>
              <button
                type="button"
                onClick={() => {
                  setSharingButtonTab('react');
                  setPromptExpanded(false);
                }}
                className={`flex-1 rounded-sm border p-1 text-[10px] font-medium transition-colors ${
                  sharingButtonTab === 'react'
                    ? 'border-edge bg-paper-light text-ink-secondary'
                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                }`}
              >
                React Button
              </button>
              <button
                type="button"
                onClick={() => {
                  setSharingButtonTab('prompt');
                  setPromptExpanded(true);
                }}
                className={`flex-1 rounded-sm border p-1 text-[10px] font-medium transition-colors ${
                  sharingButtonTab === 'prompt'
                    ? 'border-edge bg-paper-light text-ink-secondary'
                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                }`}
              >
                AI Prompt
              </button>
            </div>
            <div className="flex gap-1 items-stretch">
              <div
                key={`share-snippet-${elementID}-${sharingActiveKey}`}
                role="button"
                tabIndex={0}
                className={`relative flex min-h-[2.25rem] flex-1 rounded-md border border-edge bg-paper-inset px-2.5 py-1.5 font-mono text-ink-secondary break-all select-all ${
                  isPromptSharing
                    ? `items-start whitespace-pre-wrap ${
                        promptExpanded ? 'cursor-not-allowed' : 'cursor-pointer'
                      }`
                    : 'items-center cursor-text text-[10px]'
                }`}
                style={isPromptSharing && !promptExpanded ? { maxHeight: '2.25rem' } : undefined}
                onClick={() => {
                  if (isPromptSharing && !promptExpanded) {
                    setPromptExpanded(true);
                    return;
                  }
                  selectSnippetNode(snippetId);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (isPromptSharing && !promptExpanded) {
                      setPromptExpanded(true);
                      return;
                    }
                    selectSnippetNode(snippetId);
                  }
                }}
              >
                <span id={snippetId} className={isPromptSharing ? 'text-[10px] leading-relaxed' : ''}>
                  {activeSharing.snippet}
                </span>
                {isPromptSharing && !promptExpanded && (
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-paper-inset to-transparent" />
                )}
              </div>
              <button
                type="button"
                className="flex shrink-0 items-center gap-1 self-stretch rounded-md border border-edge px-2 text-[11px] font-medium text-ink-muted transition-colors hover:bg-paper-inset hover:text-ink-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleCopySharing();
                }}
                title="Copy to clipboard"
              >
                <CopyIcon />
                Copy
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Resolve title and tooltip
  const displayTitle = title ?? 'Overlay Trigger';
  const showTooltip = tooltipContent !== null;
  const defaultTooltip = isUrlElement ? (
    <p>
      Add a button or link to your website that opens the selected form in the desired destination.
      Your site must have the CharityStack script installed for these to work.
    </p>
  ) : (
    <p>
      Add a button or link to your website that opens this form as a popup overlay. Your site must
      have the CharityStack script installed for these to work.
      {!hasWebsiteUrl && (
        <span className="block mt-1 font-medium text-red-400">
          Note: Your overlay elements are not protected by domain verification. Add your website URL
          in Settings &rarr; Organization to restrict overlays to your domain.
        </span>
      )}
    </p>
  );
  const resolvedTooltip = tooltipContent ?? defaultTooltip;

  return (
    <div>
      {showHeader !== false && (
        <div className="flex items-center gap-1.5 mb-2">
          <Label className="text-sm font-medium text-ink">{displayTitle}</Label>
          {showTooltip && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InformationCircleIcon className="h-4 w-4 cursor-help text-ink-muted hover:text-ink-secondary" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {resolvedTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      {noDomainWarning}

      {/* Top-level Tabs */}
      <div className="rounded-lg border border-edge bg-paper-inset mb-2">
        <nav className="flex w-full p-0.5" aria-label="Overlay trigger tabs">
          {TOP_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTopTab(tab.key);
                setPromptExpanded(tab.key === 'prompt');
              }}
              className={`flex-1 whitespace-nowrap rounded-md p-1 text-xs font-medium transition-colors ${
                activeTopTab === tab.key
                  ? 'border border-edge bg-white text-ink'
                  : 'border border-transparent bg-transparent text-ink-muted hover:text-ink-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-tabs for HTML and React */}
      {hasSubTabs && (
        <div className="flex gap-1 mb-2">
          {subTabs.map((tab) => {
            const isActive = activeKey === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (activeTopTab === 'html') setHtmlSubTab(tab.key);
                  else setReactSubTab(tab.key);
                }}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? 'bg-ink/10 text-ink'
                    : 'text-ink-muted hover:text-ink-secondary hover:bg-ink/5'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      {pagePathInput}

      {/* Snippet display */}
      <div className="relative w-full">
        <div
          key={`overlay-${elementID}-${activeKey}`}
          role="button"
          tabIndex={0}
          className={`flex min-h-[3rem] w-full rounded-md border border-edge bg-paper-inset px-3 ${
            activeTopTab === 'url' ? 'pr-24' : 'pr-12'
          } py-2 text-xs font-mono break-all select-all ${
            activeTopTab === 'prompt'
              ? `whitespace-pre-wrap items-start relative overflow-hidden ${
                  promptExpanded ? 'cursor-not-allowed' : 'cursor-pointer'
                }`
              : 'items-center cursor-not-allowed'
          }`}
          style={activeTopTab === 'prompt' && !promptExpanded ? { maxHeight: '3rem' } : undefined}
          onClick={() => {
            if (activeTopTab === 'prompt' && !promptExpanded) {
              setPromptExpanded(true);
              return;
            }
            const sel = window.getSelection();
            const range = document.createRange();
            range.selectNodeContents(document.getElementById(`snippet-${elementID}-${activeKey}`)!);
            sel?.removeAllRanges();
            sel?.addRange(range);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (activeTopTab === 'prompt' && !promptExpanded) {
                setPromptExpanded(true);
                return;
              }
              const sel = window.getSelection();
              const range = document.createRange();
              range.selectNodeContents(
                document.getElementById(`snippet-${elementID}-${activeKey}`)!,
              );
              sel?.removeAllRanges();
              sel?.addRange(range);
            }
          }}
        >
          <span id={`snippet-${elementID}-${activeKey}`}>{active.snippet}</span>
          {activeTopTab === 'prompt' && !promptExpanded && (
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-paper-inset to-transparent pointer-events-none" />
          )}
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {activeTopTab === 'url' && active.snippet.startsWith('http') && (
            <button
              type="button"
              className="flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
              onClick={() => window.open(active.snippet, '_blank', 'noopener,noreferrer')}
              title="Open in New Tab"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
          <button
            type="button"
            className="flex items-center justify-center rounded-md p-1.5 text-ink-secondary hover:bg-paper-inset hover:text-ink"
            onClick={handleCopy}
            title="Copy to clipboard"
          >
            <svg
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.875 3.19409C7.36058 3.19409 6.92546 3.53984 6.79207 4.01271C6.76478 4.10947 6.75 4.21209 6.75 4.31909H11.25C11.25 4.21209 11.2352 4.10947 11.2079 4.01271C11.0745 3.53984 10.6394 3.19409 10.125 3.19409H7.875ZM5.8549 3.32751C6.22133 2.58236 6.98769 2.06909 7.875 2.06909H10.125C11.0123 2.06909 11.7787 2.58236 12.1451 3.32751C12.5183 3.35931 12.8899 3.39672 13.2598 3.43965C14.3829 3.56998 15.1875 4.53451 15.1875 5.63683V15.5691C15.1875 16.8117 14.1801 17.8191 12.9375 17.8191H5.0625C3.81986 17.8191 2.8125 16.8117 2.8125 15.5691V5.63683C2.8125 4.53451 3.61712 3.56998 4.74017 3.43965C5.1101 3.39672 5.48171 3.35931 5.8549 3.32751Z"
                fill="#3A3429"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
